import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthContext";
import { Header, Button, List, Grid } from "semantic-ui-react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  AVAILABILITY,
  CREATE_AVAILABILITY,
  UPDATE_AVAILABILITY,
} from "../graphQueries";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./style.css";
import moment from "moment";
import { toast } from "react-toastify";

const Availability = () => {
  const { loginContext } = useContext(AuthContext);
  const isAdmin = loginContext.role === "Admin";
  const [scheduleDays, setScheduleDays] = useState([]);
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [availabilityId, setAvailabilityId] = useState("");
  const [tempTimeOffArray, setTempTimeOffArray] = useState([]);

  const [availability] = useLazyQuery(AVAILABILITY, {
    fetchPolicy: "network-only",
    onCompleted: (result) => handleAvailability_Result(result),
    onError: (error) => handleAvailability_Error(error),
  });

  const [createAvailability] = useMutation(CREATE_AVAILABILITY, {
    onCompleted: (result) => handleCreateAvailability_Result(result),
    onError: (error) => handleCreateAvailability_Error(error),
  });

  const [updateAvailability] = useMutation(UPDATE_AVAILABILITY, {
    onCompleted: (result) => handleUpdateAvailability_Result(result),
    onError: (error) => handleUpdateAvailability_Error(error),
  });

  useEffect(() => {
    availability();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      scrollDatePickerTimeList();
    }, 200);
  }, []);

  const handleDaySelect = (day) => {
    setDisabled(false);
    const array = [...scheduleDays];
    if (array.includes(day)) {
      setScheduleDays(array.filter((x) => x !== day));
    } else {
      array.push(day);
      setScheduleDays(array);
    }
  };

  const getActiveDay = (day) => scheduleDays.includes(day);

  const handleAvailability_Result = (result) => {
    if (result && result.availability.length) {
      const obj = result.availability[0];
      const array = obj.days.split(",");
      setScheduleDays(array);
      setScheduleStart(new Date(obj.start));
      setScheduleEnd(new Date(obj.end));
      setAvailabilityId(obj.availabilityId);
      if (obj.daysOff) {
        const daysOffArray = obj.daysOff.split(",");
        setTempTimeOffArray(daysOffArray.map((d) => new Date(d)));
      }
    }
  };

  const handleAvailability_Error = (error) => {
    toast.error(error.message || "Failed to fetch availability.");
  };

  const handleCreateAvailability_Result = (result) => {
    setSaving(false);
    if (
      result.createAvailability &&
      result.createAvailability.status === "succeeded"
    ) {
      toast.success("Availability saved.");
      availability();
    }
  };

  const handleCreateAvailability_Error = (error) => {
    setSaving(false);
    toast.error(error.message || "Failed to save availability.");
  };

  const handleUpdateAvailability_Result = (result) => {
    setSaving(false);
    if (
      result.updateAvailability &&
      result.updateAvailability.status === "succeeded"
    ) {
      toast.success("Availability saved.");
    }
  };

  const handleUpdateAvailability_Error = (error) => {
    setSaving(false);
    toast.error(error.message || "Failed to update availability.");
  };

  const handleCreateAvailability = () => {
    if (!scheduleDays.length) {
      alert("Please select days available.");
      return;
    }
    if (!scheduleStart) {
      alert("Please select a start time.");
      return;
    }
    if (!scheduleEnd) {
      alert("Please select and end time.");
      return;
    }
    setSaving(true);
    setDisabled(true);
    setTimeout(() => {
      if (availabilityId) {
        updateAvailability({
          variables: {
            availabilityId,
            days: scheduleDays.join(","),
            start: moment(scheduleStart).utc().format(),
            end: moment(scheduleEnd).utc().format(),
            daysOff: tempTimeOffArray.join(","),
          },
        });
      } else {
        createAvailability({
          variables: {
            days: scheduleDays.join(","),
            start: moment(scheduleStart).utc().format(),
            end: moment(scheduleEnd).utc().format(),
            daysOff: tempTimeOffArray.join(","),
          },
        });
      }
    }, 1000);
  };

  const scrollDatePickerTimeList = () => {
    // This selector should only target time lists inside react-datepicker
    const selectedItems = document.querySelectorAll(
      ".react-datepicker__time-list-item--selected"
    );
    selectedItems.forEach((el) => {
      // Make sure to use block: 'nearest' and behavior: 'auto'
      el.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "auto",
      });
    });
  };

  const handleChangeTimeOff = (dates) => {
    setDisabled(false);
    const sorted = dates.sort((a, b) => a - b);
    setTempTimeOffArray(sorted);
  };

  const handleClearDaysOff = () => {
    setTempTimeOffArray([]);
    setDisabled(false);
  };

  const DaysOff = () => {
    const sorted = tempTimeOffArray.sort((a, b) => a - b);
    return sorted.map((item, index) => (
      <List.Item key={index}>{moment(item).format("LL")}</List.Item>
    ));
  };

  return (
    <div
      style={{
        width: "100vw",
        overflow: "auto",
        backgroundColor: "#F1F1F1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: 0,
        margin: 0,
        boxSizing: "border-box",
      }}
    >
      {/* This is the "card" or container for your actual form */}
      <div
        style={{
          background: "white",
          maxWidth: "100%",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          padding: "2.5rem 2.5rem",
        }}
      >
        <Header
          as="h1"
          content="Availability"
          subheader="Set your availability by selecting the days of the week you wish to work, and the time frame you are available. Your availability will determine if attorneys are able to request your services."
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div
            style={{
              backgroundColor: "#F1F1F1",
              padding: 20,
              borderRadius: 20,

              boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
            }}
          >
            <Header
              content="Days"
              subheader="Choose the days you'll be available."
            />
            <Button.Group>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <Button
                  key={d}
                  primary={getActiveDay(d)}
                  content={d}
                  onClick={() => handleDaySelect(d)}
                />
              ))}
            </Button.Group>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 20,
              width: "100%",
            }}
          >
            <div
              style={{
                backgroundColor: "#F1F1F1",
                padding: 20,
                borderRadius: 20,
                marginBottom: 20,
                boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
                flex: 1,
              }}
            >
              <Header
                content="Hours"
                subheader="Choose the hours you'll be available on the above days."
              />
              <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Header as="h4">Start</Header>
                  <DatePicker
                    inline
                    wrapperClassName="ui input"
                    selected={scheduleStart}
                    onChange={(date) => {
                      setScheduleStart(date);
                      setDisabled(false);
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Header as="h4">End</Header>
                  <DatePicker
                    inline
                    selected={scheduleEnd}
                    onChange={(date) => {
                      setScheduleEnd(date);
                      setDisabled(false);
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#F1F1F1",
                padding: 20,
                borderRadius: 20,
                marginBottom: 20,
                boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
                flex: 1,
              }}
            >
              <Header
                content="Time Off"
                subheader="Choose any specific dates to be unavailable. These will override the above schedule."
              />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <DatePicker
                  inline
                  closeOnScroll={true}
                  placeholderText="Select a Date"
                  dateFormat="MMMM d, yyyy"
                  selectedDates={tempTimeOffArray}
                  onChange={(dates) => handleChangeTimeOff(dates || [])}
                  minDate={new Date()}
                  fixedHeight
                  shouldCloseOnSelect={false}
                  selectsMultiple
                  disabledKeyboardNavigation
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    marginLeft: "1rem",
                  }}
                >
                  <List style={{ maxHeight: "180px", overflowY: "auto" }}>
                    <List.Header
                      style={{ fontWeight: 800, textDecoration: "underline" }}
                    >
                      Selected Days
                    </List.Header>
                    {tempTimeOffArray.length > 0 ? <DaysOff /> : "None"}
                  </List>
                  <Button
                    icon="close"
                    secondary
                    content="Clear Days Off"
                    onClick={handleClearDaysOff}
                    style={{ marginTop: "1rem" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          background:
            "linear-gradient(to top, rgba(241,241,241,0.98) 80%, rgba(241,241,241,0.0))",
          padding: "1.5rem 0",
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 300,
            pointerEvents: "auto",
            padding: "0 2rem",
          }}
        >
          <Button
            icon="save"
            disabled={disabled}
            loading={saving}
            content="Save Changes"
            size="large"
            onClick={handleCreateAvailability}
            positive
          />
        </div>
      </div>
    </div>
  );
};

export default Availability;
